import { useTranslation } from 'react-i18next';
import EmptyState from '../Fundamentals/EmptyState/EmptyState';
import SpirisIcon from '../Fundamentals/Icon/SpirisIcon';
import SegmentedControl from '../Fundamentals/SegmentedControl/SegmentedControl';
import PackageStructurePackageRow from './PackageStructurePackageRow';
import type { DisplayPackage } from './PriceSimulation.types';

export interface PackageStructureProps {
    packages?: DisplayPackage[];
    expandedPackages: Record<string, boolean>;
    expandedTiers: Record<string, boolean>;
    onTogglePackage: (packageId: string) => void;
    onToggleTier: (tierId: string) => void;
    isVisible: boolean;
    onToggleVisibility: () => void;
    selectedPeriod?: number;
    onPeriodChange?: (value: number) => void;
}

const BILLING_CYCLES = [
    { value: 1, label: '1M' },
    { value: 3, label: '3M' },
    { value: 12, label: '12M' },
];

const PackageStructure = ({
    packages = [],
    expandedPackages,
    expandedTiers,
    onTogglePackage,
    onToggleTier,
    isVisible,
    onToggleVisibility,
    selectedPeriod = 12,
    onPeriodChange,
}: PackageStructureProps) => {
    const { t } = useTranslation();

    const toggleButtonText = isVisible ? t('priceSimulation.hidePackageStructure') : t('priceSimulation.showPackageStructure');

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-16">
                <div className="d-flex align-items-center gap-8">
                    <SpirisIcon name="article" />
                    <h3 className="heading-lg mb-0">{t('priceSimulation.packageStructure')}</h3>
                </div>
                <div className="d-flex align-items-center gap-16">
                    <SegmentedControl
                        options={BILLING_CYCLES}
                        value={selectedPeriod}
                        onChange={(value) => onPeriodChange?.(value)}
                        ariaLabel={t('priceSimulation.billingCycle')}
                        size="sm"
                    />
                    <button type="button" className="mb-0 btn btn-link btn-sm" aria-expanded={isVisible} onClick={onToggleVisibility}>
                        <SpirisIcon name={isVisible ? 'chevron-up' : 'chevron-down'} className="mr-4" />
                        {toggleButtonText}
                    </button>
                </div>
            </div>

            {isVisible && (
                <div className="d-flex flex-column gap-16">
                    {packages.length === 0 ? (
                        <EmptyState icon="calculator" size="sm" title={t('priceSimulation.noPackagesMatch')} />
                    ) : (
                        packages.map((pkg) => (
                            <PackageStructurePackageRow
                                key={pkg.id}
                                package={pkg}
                                isExpanded={expandedPackages[pkg.id] || false}
                                onToggle={() => onTogglePackage(pkg.id)}
                                expandedTiers={expandedTiers}
                                onToggleTier={onToggleTier}
                            />
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default PackageStructure;
